import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import useAsyncAbortableRequest from "hooks/useAsyncAbortableRequest";
import { LoadablePage } from "shared/LoadablePage";
import type { Subscription } from "client/api-v2";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { SubscriptionForm } from "../../components/SubscriptionForm";
import type { SubscriptionFormData } from "../../components/SubscriptionForm";
import { SubscriptionService, getSubscriptionById, getPlanById } from "../../services/subscriptionService";
import { subscriptionRoutes } from "../../subscription-routes";
import { lifecycleSupportsEntitlementOverrides } from "../../utils/plan-utils";

export function SubscriptionEditForm() {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { refresh } = useEntity<Subscription>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ message: string; severity: "success" | "error" } | null>(null);
  const [planId, setPlanId] = useState<string>("");

  const { data: subscription, loading, error } = useAsyncAbortableRequest(getSubscriptionById, subscriptionId || "");

  // Update planId when subscription loads
  useEffect(() => {
    if (subscription?.planId && subscription.planId !== planId) {
      setPlanId(subscription.planId);
    }
  }, [subscription?.planId, planId]);

  const { data: plan, loading: planLoading, error: planError } = useAsyncAbortableRequest(getPlanById, planId);

  const handleSubmit = async (data: SubscriptionFormData) => {
    if (!subscriptionId) return;

    setIsSubmitting(true);

    try {
      const trimmedFriendlyName = data.friendlyName?.trim() || undefined;
      const friendlyNameChanged = trimmedFriendlyName !== subscription?.friendlyName;
      const planChanged = data.planId && data.planId !== subscription?.planId;

      // Detect term end date change for paid subscriptions
      // Note: subscription.termEndDate is typed as string but is a Luxon DateTime at runtime (axios interceptor deserializes dates)
      const termEndDateChanged =
        subscription?.isTermEndDateEditable &&
        data.termEndDate?.isValid &&
        data.termEndDate.toISODate() !==
          (subscription?.termEndDate ? getOrParseDateTime(subscription.termEndDate).toISODate() : undefined);

      // Handle plan change separately if needed
      if (planChanged && data.planId) {
        await SubscriptionService.changeSubscriptionPlan(subscriptionId, data.planId);
      }

      // Handle other updates
      if (friendlyNameChanged || data.user?.id !== subscription?.ownerUserId || termEndDateChanged) {
        await SubscriptionService.updateSubscription(subscriptionId, {
          ...(friendlyNameChanged && { friendlyName: trimmedFriendlyName }),
          ownerUserId: data.user?.id || null,
          ...(termEndDateChanged && { termEndDate: data.termEndDate?.toISODate() }),
        });
      }

      // Refresh the subscription data in the parent context
      if (refresh) {
        await refresh();
      }

      // Check if plan was changed and lifecycle supports overrides to show different message
      const planChangedWithOverrides =
        planChanged && subscription?.lifecycleType && lifecycleSupportsEntitlementOverrides(subscription.lifecycleType);

      if (planChangedWithOverrides) {
        setAlert({
          message: data.plan?.clearsEntitlementOverrides
            ? "Plan changed successfully. Your entitlement overrides were cleared, because this plan grants the maximum of every limit."
            : "Plan changed successfully. Your entitlement overrides have been preserved. Use 'Edit Entitlement Overrides' to customize.",
          severity: "success",
        });
        setTimeout(() => navigate(subscriptionRoutes.detail(subscriptionId).root), 3000);
      } else {
        setAlert({ message: "Subscription updated successfully", severity: "success" });
        setTimeout(() => navigate(subscriptionRoutes.detail(subscriptionId).root), 1500);
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      setAlert({ message: "Failed to update subscription. Please try again.", severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (subscriptionId) {
      navigate(subscriptionRoutes.detail(subscriptionId).root);
    } else {
      navigate(subscriptionRoutes.root);
    }
  };

  if (!subscriptionId) {
    return <div>Subscription ID is required</div>;
  }

  return (
    <LoadablePage error={error || planError}>
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}
      {subscription && (
        <SubscriptionForm
          mode="edit"
          initialData={subscription}
          selectedPlan={plan || undefined}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading || planLoading || isSubmitting}
        />
      )}
    </LoadablePage>
  );
}
